import React, { useEffect, useState } from 'react';
import { Box, Grid, Typography, Paper } from '@mui/material';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';
import { showLoader } from '../../../../Redux/Slices/loaderSlice';
import ButtonContained from '../../../Common/ButtonContained';
import CommonTextField from '../../../Common/CommonTextField';
import TextLabel from '../../../Common/CommonTextLabel';
import ErrorHandler from '../../../../Utils/handler';
import { ApiGet, ApiPost } from '../../../../ApiSetup/Api';

const PpskId = () => {
    const dispatch = useDispatch();
    
    const [receiptYear, setReceiptYear] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    const handleChange = (e) => {
        const value = e.target.value;
        // Only allow 4 digit years
        if (value === '' || (/^\d{0,4}$/.test(value))) {
            setReceiptYear(value);
            setError('');
        }
    };

    const validate = () => {
        if (!receiptYear || receiptYear.length !== 4) {
            setError('Please enter a valid 4-digit year');
            return false;
        }
        const year = parseInt(receiptYear);
        if (year < 2000 || year > 2100) {
            setError('Year must be between 2000 and 2100');
            return false;
        }
        return true;
    };

    const _getReceiptYear = async () => {
        setIsLoading(true);
        dispatch(showLoader(true));
        try {
            const res = await ApiGet('admin/get_receipt_year');
            if (res?.data?.receiptYear) {
                setReceiptYear(res.data.receiptYear.toString());
            }
        } catch (err) {
            // If no receipt year found, use current year
            setReceiptYear(new Date().getFullYear().toString());
        } finally {
            setIsLoading(false);
            dispatch(showLoader(false));
        }
    };

    const _updateReceiptYear = async () => {
        if (!validate()) return;
        
        dispatch(showLoader(true));
        try {
            const res = await ApiPost('admin/update_receipt_year', { year: parseInt(receiptYear) });
            if (res?.data?.receipt) {
                toast.success('Receipt year updated successfully');
                _getReceiptYear();
            } else {
                toast.error(res?.data?.message || 'Failed to update receipt year');
            }
        } catch (err) {
            ErrorHandler(err);
        } finally {
            dispatch(showLoader(false));
        }
    };

    useEffect(() => {
        _getReceiptYear();
    }, []);

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 500 }}>
                        PPSK ID Configuration
                    </Typography>
                </Box>
            </Grid>

            <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '10px' }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                        Receipt Year Settings
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                        Configure the receipt year for PPSK ID generation. This year will be used as a prefix for all new PPSK IDs generated.
                    </Typography>
                    
                    <Grid container spacing={2} alignItems="flex-end">
                        <Grid item xs={12} md={4}>
                            <TextLabel title="Receipt Year" required />
                            <CommonTextField
                                value={receiptYear}
                                onChange={handleChange}
                                placeholder="Enter year (e.g., 2025)"
                                error={!!error}
                                helperText={error}
                                type="text"
                                inputProps={{ maxLength: 4 }}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <ButtonContained 
                                text="Update Year" 
                                onClick={_updateReceiptYear}
                                disabled={isLoading}
                            />
                        </Grid>
                    </Grid>
                </Paper>
            </Grid>

            <Grid item xs={12}>
                <Paper elevation={0} sx={{ p: 3, border: '1px solid #e0e0e0', borderRadius: '10px', mt: 2 }}>
                    <Typography variant="h6" sx={{ mb: 2, fontWeight: 500 }}>
                        PPSK ID Format Information
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        PPSK IDs are generated automatically in the format: <strong>PPSK{receiptYear || 'YYYY'}XXXXX</strong>
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        Where XXXXX is an auto-incrementing number starting from 00001 each year.
                    </Typography>
                </Paper>
            </Grid>
        </Grid>
    );
};

export default PpskId;
